import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    question: 'What exactly is a Lady Bird Deed?',
    answer: 'A Lady Bird Deed, also known as an enhanced life estate deed, is a special type of deed that allows you to name beneficiaries who will automatically inherit your property upon your passing — while you retain full control during your lifetime. You can sell, mortgage, or even cancel the deed entirely without needing permission from your beneficiaries.'
  },
  {
    question: 'How does a Lady Bird Deed avoid probate?',
    answer: 'When you pass away, your chosen beneficiaries immediately gain access to the property without going through probate court. They simply need to obtain a certified death certificate, record it with the county, and update property records. This is typically much faster and simpler than a court-supervised probate process.'
  },
  {
    question: 'Can I change or revoke a Lady Bird Deed later?',
    answer: 'Absolutely! Since Lady Bird Deeds are fully revocable, you can change beneficiaries or cancel the deed entirely during your lifetime by recording a new document. This flexibility is one of the key advantages over other estate planning tools.'
  },
  {
    question: 'Will a Lady Bird Deed affect my property taxes or homestead exemption?',
    answer: 'In Florida, a properly drafted Lady Bird Deed typically preserves your homestead protections and does not trigger property tax reassessment during your lifetime. However, improper drafting can affect these valuable exemptions — which is why working with an experienced attorney is essential.'
  },
  {
    question: 'Do I need my mortgage lender\'s approval?',
    answer: 'Generally, no. Since ownership doesn\'t actually transfer during your lifetime, most mortgages are unaffected. However, it\'s always wise to review your loan terms beforehand. We can help you understand how your specific mortgage might be impacted.'
  },
  {
    question: 'How is a Lady Bird Deed different from a regular deed or will?',
    answer: 'Unlike a will (which must go through probate), a Lady Bird Deed transfers property automatically upon death. Unlike a regular deed, you retain complete control and can revoke it anytime. It\'s often a simpler alternative to setting up a trust specifically for real estate transfer.'
  },
  {
    question: 'Does Florida allow Transfer-on-Death (TOD) deeds?',
    answer: 'No. Florida law does not authorize Transfer-on-Death (TOD) deeds for real estate. Florida homeowners must use alternatives like Lady Bird Deeds or trusts to pass property outside of probate. Using a TOD deed form in Florida would not achieve the intended goal.'
  },
  {
    question: 'What happens if my named beneficiary passes away before me?',
    answer: 'If a named beneficiary dies before you, the outcome can depend on state law or may require probate involvement. A properly drafted Lady Bird Deed can address this situation by including contingent beneficiaries or other provisions to ensure your wishes are honored.'
  },
  {
    question: 'How much does a Lady Bird Deed cost in Florida?',
    answer: 'In Florida, you can expect clear, upfront pricing. For a simple Lady Bird Deed, costs typically range from approximately $800 to $1,000. More complex situations, such as multiple owners or title issues, may incur higher fees. Additional costs might include modest county recording fees or a title review if needed.'
  },
  {
    question: 'Is a Lady Bird Deed right for me?',
    answer: 'Lady Bird Deeds are an excellent choice for Florida homeowners who want a simple, effective way to pass their home directly to heirs while avoiding probate. However, they\'re most effective as part of a comprehensive estate plan. Schedule a consultation so we can assess your specific situation and goals.'
  },
];

const FAQSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" className="relative py-24 lg:py-32 bg-[#0f0f0f]" ref={sectionRef}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div 
          className={`text-center mb-16 ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#c9a87c] mb-4 block">
            Common Questions
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#faf9f7] mb-6">
            Frequently Asked <span className="text-gradient-gold">Questions</span>
          </h2>
          <p className="font-sans text-lg text-[#faf9f7]/60 max-w-2xl mx-auto">
            Get answers to the most common questions about Lady Bird Deeds and how they can protect your Florida home.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div 
          className={`${
            isVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'
          }`}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-[#141414] border border-[#c9a87c]/10 rounded-lg overflow-hidden data-[state=open]:border-[#c9a87c]/30 transition-colors duration-300"
              >
                <AccordionTrigger className="px-6 py-5 text-left hover:no-underline group">
                  <span className="font-serif text-lg text-[#faf9f7] group-hover:text-[#c9a87c] transition-colors duration-300 pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5">
                  <p className="font-sans text-[#faf9f7]/70 leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Bottom note */}
        <div 
          className={`text-center mt-12 ${
            isVisible ? 'animate-fade-in-up delay-400' : 'opacity-0'
          }`}
        >
          <p className="font-sans text-[#faf9f7]/50">
            Have more questions? <a href="#contact" className="text-[#c9a87c] hover:text-[#d4b896] link-underline">Contact us</a> for personalized answers.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
